import type {ReactElement} from 'react'
import {exhaustive} from '@freckle/exhaustive'
import {addMaybeClassName} from '@freckle/educator-entities/ts/common/helpers/classnames'
import type {BreadcrumbsProps} from '@freckle/educator-materials/ts/components/breadcrumbs'
import type {PageTitleT} from '@freckle/educator-materials/ts/components/page-header'
import {PageHeader} from '@freckle/educator-materials/ts/components/page-header'
import type {DisableHelperBarT} from '@freckle/classroom/ts/common/components/dashboard'
import {pageHeaderContainerWithBreadcrumbs, pageHeaderContainer} from './page-wrapper.module.scss'

type Props = {
  pageHeaderText?: PageTitleT
  secondaryHeaderContent?: ReactElement
  children: ReactElement
  disablePageHeader?: DisableHelperBarT
  breadcrumbsConfig?: BreadcrumbsProps | null | undefined
  breadcrumbsClass?: string
  headerClass?: string
  headerMarginBottom?: number
  headerContentClass?: string
  dataTestHeader?: string
}

function renderPageHeader(props: {
  pageHeaderText?: PageTitleT
  secondaryHeaderContent?: ReactElement
  disablePageHeader?: DisableHelperBarT
  breadcrumbsConfig?: BreadcrumbsProps | null | undefined
  breadcrumbsClass?: string
  headerClass?: string
  headerMarginBottom?: number
  headerContentClass?: string
  dataTestHeader?: string
}) {
  const {dataTestHeader} = props
  const disablePageHeader = props.disablePageHeader ?? 'never'

  const headerClass = addMaybeClassName(
    props.breadcrumbsConfig ? pageHeaderContainerWithBreadcrumbs : pageHeaderContainer,
    props.headerClass
  )
  const headerStyle =
    props.headerMarginBottom !== undefined ? {marginBottom: props.headerMarginBottom} : undefined
  switch (disablePageHeader) {
    case 'always':
      return <></>
    case 'never':
      return (
        <div className={headerClass} style={headerStyle}>
          <PageHeader
            title={props.pageHeaderText}
            breadcrumbsConfig={props.breadcrumbsConfig}
            breadcrumbsClass={props.breadcrumbsClass}
            addContainerClass={props.headerContentClass}
            dataTest={dataTestHeader}
          />
          {props.secondaryHeaderContent}
        </div>
      )
    case 'print':
      return (
        <div className="hidden-print">
          <div className={headerClass} style={headerStyle}>
            <PageHeader
              title={props.pageHeaderText}
              breadcrumbsConfig={props.breadcrumbsConfig}
              addContainerClass={props.headerContentClass}
            />
            {props.secondaryHeaderContent}
          </div>
        </div>
      )
    default:
      return exhaustive(disablePageHeader, 'renderPageHeader')
  }
}

export function PageWrapper(props: Props) {
  const {
    pageHeaderText,
    secondaryHeaderContent,
    children,
    disablePageHeader,
    breadcrumbsConfig,
    breadcrumbsClass,
    headerClass,
    headerMarginBottom,
    headerContentClass,
    dataTestHeader
  } = props
  return (
    <>
      {renderPageHeader({
        pageHeaderText,
        secondaryHeaderContent,
        disablePageHeader,
        breadcrumbsConfig,
        breadcrumbsClass,
        headerClass,
        headerMarginBottom,
        headerContentClass,
        dataTestHeader
      })}
      <div className="row no-margin-row">
        <div id="inner-content" className="col-12">
          {children}
        </div>
      </div>
    </>
  )
}
